import { Button, IconButton } from "@ai-teach/ui-web";
import { useLayoutEffect, useRef } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useSlideEditorContext } from "../editor/SlideEditorContext";
import type { NavLabel } from "../toolbar/SlideToolbar";
import { DisabledPanelWrapper } from "./DisabledPanelWrapper";
import { GraphicsPanel } from "./panels/GraphicsPanel";
import { LayersPanel } from "./panels/LayersPanel";
import { MyStylesPanel } from "./panels/MyStylesPanel";
import { PhonicsPanel } from "./panels/PhonicsPanel";
import { QuestionsPanel } from "./panels/QuestionsPanel";
import { ShapesPanel } from "./panels/ShapesPanel";
import { TextPanel } from "./panels/TextPanel";
import { MediaPanel } from "./panels/medias/MediaPanel";

// Kept per session so reopening a panel returns to where the teacher left off.
const panelScrollTop = new Map<string, number>();

export function ToolbarPanelContainer() {
  const { isLiveView, activePanel, setActivePanel } = useSlideEditorContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const shownPanel = useRef<string | null>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !activePanel) return;
    el.scrollTop = panelScrollTop.get(activePanel) ?? 0;
    shownPanel.current = activePanel;
    return () => {
      if (scrollRef.current && shownPanel.current) {
        panelScrollTop.set(shownPanel.current, scrollRef.current.scrollTop);
      }
    };
  }, [activePanel]);

  return (
    <div
      ref={scrollRef}
      data-element-toolbar="true"
      onScroll={() => {
        if (activePanel && scrollRef.current) {
          panelScrollTop.set(activePanel, scrollRef.current.scrollTop);
        }
      }}
      className={`relative bg-background w-[450px] h-full  z-[900] ${isLiveView ? "overflow-hidden" : "overflow-auto"}`}
    >
      {/* Header with modern glassmorphism effect */}
      <div className="sticky top-0 z-10 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
        <div className="flex items-center gap-3 px-4 py-4">
          <IconButton
            onClick={() => setActivePanel(null)}
            variant="ghost"
            className="group flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-slate-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
            aria-label="Go back"
          >
            <FaArrowLeft className="h-5 w-5" color="#000000" />
          </IconButton>
          <p className="text-lg font-bold tracking-tight">{activePanel ?? ""}</p>
        </div>
      </div>

      {/* Content area with padding */}
      <div className="px-2 py-4">
        <DisabledPanelWrapper isDisabled={isLiveView}>
          {activePanel === "Shapes" && <ShapesPanel />}
          {activePanel === "Graphics" && <GraphicsPanel />}
          {activePanel === "Text" && <TextPanel />}
          {activePanel === "Media" && <MediaPanel />}
          {activePanel === "Phonics" && <PhonicsPanel />}
          {activePanel === "Styles" && <MyStylesPanel />}
          {activePanel === "Questions" && <QuestionsPanel />}
          {activePanel === "Layers" && <LayersPanel />}
        </DisabledPanelWrapper>
      </div>
    </div>
  );
}
